import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { navItemById } from '../routes/navigation';

/**
 * The page name shown in the Hub's top bar.
 *
 * Resolved in tiers, first match wins:
 *
 *   1. an override a page set after a fetch — Recording Detail showing the
 *      session's own name, which nothing knows at navigation time
 *   2. `meta.title` — a route that belongs to a section but names itself
 *   3. the active nav item's label — the default, and what makes the title
 *      and the highlighted sidebar item the same fact rather than two
 *      strings someone has to keep in step
 *   4. the route name, as a last resort
 *
 * Module-scoped on purpose: the page setting the title and the bar rendering
 * it are in different subtrees, and passing a title up through a router-view
 * would mean every page knowing about the shell.
 */
const override = ref(null);

/**
 * Give the current page a title only it can know. Pass null to clear.
 * Cleared automatically on navigation, so a page that forgets cannot leave a
 * stale title on the next one.
 */
export function setPageTitle(title) {
  override.value = title || null;
}

export function useHubPageTitle() {
  const route = useRoute();

  // Clear on initialisation as well as on navigation. The shell unmounts
  // whenever the viewer visits a page outside it — /hub, a preview, the login
  // screen — and a module-scoped override would otherwise survive that and
  // name a completely unrelated page when the shell came back.
  override.value = null;

  watch(
    () => route.fullPath,
    () => {
      override.value = null;
    }
  );

  return computed(
    () =>
      override.value ??
      route.meta?.title ??
      navItemById(route.meta?.navItem)?.label ??
      route.name ??
      ''
  );
}
